<script setup lang="ts">
/**
 * The settings window — graphics, audio and aim, as one dialog used in two
 * places.
 *
 * ── Why it is a component and not a `Teleport` ───────────────────────────────
 *
 * It has two homes: the battle HUD's escape menu and the garage title bar's
 * gear. Teleporting one instance between them raced on close — the teleport's
 * `disabled` and the host's own `v-if` flip in the same tick, and Vue was left
 * inserting into a parent that had just gone (`insertBefore` of null). One
 * definition rendered in two places has no such ordering, and every setting it
 * binds is a module-scope ref in `state/settings.ts`, so two instances are two
 * views of one state rather than two copies of it.
 *
 * ── Why it is a window rather than a dropdown ────────────────────────────────
 *
 * It used to be a 17rem corner panel in the arena and a 17rem dropdown under
 * the garage bar: eleven controls stacked in one column, each choice a
 * 0.16rem-tall row of 0.68rem buttons. Two things were wrong with that. It was
 * unreadable on a landscape phone — the whole column is taller than 375px of
 * screen, so the reset button was reachable only by scrolling a dropdown — and
 * it made the *battle* case two overlapping windows, because the escape menu
 * rendered this panel inside its own card while a second instance could sit in
 * the corner behind it.
 *
 * So it is one centred dialog, laid out **wide rather than tall**: controls and
 * audio side by side, video as a 2x2 below them. That shape is chosen for the
 * hardest viewport rather than the easiest — a phone held sideways is ~670x375,
 * which has width to spare and almost no height, and it is the same shape a
 * desktop wants at four times the size.
 *
 * ── The shape on a phone is one column of split groups ──────────────────────
 *
 * Compact stacks the three groups and splits each one a quarter to three
 * quarters: its name and one sentence in the quarter, its rows in the rest. The
 * full-height reasoning, including what the three-column layout it replaced
 * bought and what it cost, is in the `max-height: 520px` block at the foot of
 * the stylesheet. Two things about it reach up here: every group's prose has a
 * **second, shorter** paragraph written for a ~160px column (`.brief` beside
 * `.hint`, and `settings-panel.test.ts` refuses to let either go missing), and
 * nothing in a group is centred or stretched to a neighbour's height any more.
 *
 * ── What the host owns ──────────────────────────────────────────────────────
 *
 * The `actions` slot, and nothing else. In battle that is Resume and Leave; in
 * the garage on a phone it is the account block the title bar had to shed.
 * Putting either *inside* this component would put a Sign out button in the
 * middle of a battle, or a Leave battle button in the garage.
 *
 * **The Escape key is the host's too.** The battle already owns a window-level
 * listener that *toggles* its menu, so a second listener here would fight it:
 * the panel's close and the host's toggle both fire on one keypress and cancel
 * out, leaving a menu that cannot be shut. The hosts install their own.
 */
import {
  aimSensitivity,
  audioMuted,
  BACKGROUND_QUALITIES,
  backgroundQuality,
  EFFECTS_QUALITIES,
  effectsQuality,
  GROUND_QUALITIES,
  groundQuality,
  masterVolume,
  isDefaultSettings,
  MAX_AIM_SENSITIVITY,
  MAX_MUSIC_VOLUME,
  MAX_VOLUME,
  MIN_AIM_SENSITIVITY,
  MIN_MUSIC_VOLUME,
  MIN_VOLUME,
  musicMuted,
  musicVolume,
  previewsEnabled,
  hangarRoomEnabled,
  resetSettings,
  SKY_QUALITIES,
  skyQuality,
} from '@/state/settings';
import { fullscreen, homeScreenOfferable, immersiveSupported, toggleImmersive } from '@/state/immersive';
import { wantsTouch } from '@/game/input-source';
import { useSession } from '@/state/session';
import { navigateAdmin } from '@/state/admin-route';

/**
 * The admin panel entry, shown only to an account whose role is ADMIN.
 *
 * ── It is a link, not a permission ───────────────────────────────────────────
 *
 * `isAdmin` off the **session** decides whether the row is *drawn*. It decides
 * nothing else, and it is not trusted to: every `/api/admin/*` route is behind
 * `AdminAccessGuard`, which re-reads the account's role from the database on
 * every request. A hand-edited `true` in the browser reveals a menu row, then a
 * panel, and then a 403 from every request it makes — which the panel says out
 * loud. The correct blast radius for a flag whose only job is to avoid showing
 * every player a door they cannot open.
 *
 * Read from the session rather than the account view for one reason: the
 * account view is loaded by the profile overlay and nothing else, so it is null
 * until a player opens their profile — and it is never loaded in a battle at
 * all. `role` was already in the template's auth response and `toPlayer` was
 * dropping it, so the session answers this the moment it restores, everywhere.
 *
 * ── Why it lives in the settings window ──────────────────────────────────────
 *
 * Because this window is already the one place reachable from both the garage
 * and a battle, and an operator opens the panel from wherever they happen to
 * be. The alternative — a tab in the garage bar — spends a slot on a row that
 * is already the width problem written up under `.tabs`, for something at most
 * one account in the game ever sees.
 *
 * ── An ordinary in-app navigation ────────────────────────────────────────────
 *
 * This used to be `target="_blank"`, back when `/admin` was a different
 * application on the other side of a Caddy route and opening it in place meant
 * losing the game to a page load. The panel is a screen in this app now, so it
 * is a `pushState` like the news route: instant, no second boot, and *Back to
 * game* returns. Kottster is gone.
 */
const session = useSession();

/** Closes this window first, so the panel is not opened behind a dialog. */
function openAdmin(): void {
  emit('close');
  navigateAdmin('overview');
}

/**
 * Fullscreen, offered on every platform rather than only on a phone.
 *
 * A touch battle is *expected* to be fullscreen — a phone's URL bar and its
 * bottom gesture area are exactly where a thumb control wants to sit, and a swipe
 * up from that edge goes home, so a fire button there is a button that
 * occasionally closes the game. `App.vue` already asks on the deploy path for
 * that reason. What was missing is a way to ask for it *deliberately*, and on a
 * desktop it is worth having for its own sake: this is a pointer-lock shooter,
 * and a maximised window with browser chrome still spends 100-odd vertical pixels
 * on a URL bar the player cannot use while aiming.
 *
 * **Read live, never remembered.** `state/immersive.ts` mirrors
 * `document.fullscreenElement` through the browser's own `fullscreenchange`,
 * because fullscreen is left in ways nothing here can observe — Escape, the
 * Android back gesture, another tab taking over — and a toggle built on a
 * remembered boolean lies within seconds.
 *
 * **Hidden rather than disabled where the API is absent.** `fullscreenEnabled` is
 * `false` in an iframe without `allow="fullscreen"`, and on iOS Safari, which
 * implements this for `<video>` and not for arbitrary elements. A disabled row
 * would be a permanent apology on a platform that will never satisfy it; the same
 * rule took the dead number-key row off the HUD.
 *
 * iOS Safari is exactly that platform, and it is not left with nothing: see
 * `offerHomeScreen` below, which replaces the hidden row rather than merely
 * removing it.
 */
const canFullscreen = immersiveSupported();

/**
 * The row iOS Safari gets instead of the toggle above: not a control, since
 * there is no script API for Apple's Share sheet, but the instruction that
 * actually removes the browser chrome the toggle can't. Gated the same way
 * `FullscreenPrompt.vue` gates its own alternate branch — see
 * `homeScreenOfferable`'s own doc for why a feature check rather than a
 * platform sniff, and why it stops once the player has actually done it.
 */
const offerHomeScreen = homeScreenOfferable(wantsTouch());

defineProps<{
  /**
   * The kicker beside the title — `Battle menu` in the arena. Absent in the
   * garage, where the window is only itself.
   */
  eyebrow?: string;
}>();

const emit = defineEmits<{
  /** The X, the backdrop, or anything in the `actions` slot that dismisses. */
  (e: 'close'): void;
}>();

/**
 * Labels for the graphics tiers, cheapest last so the order reads as a cost.
 *
 * The words are the control's, not the enum's: `procedural` and `gradient` are
 * how the sky is *built*, which is a fact about `environment.ts` rather than
 * something a player is choosing between, and `reduced` does not fit a
 * segmented button beside two three-letter siblings.
 */
const SKY_LABELS: Record<string, string> = {
  procedural: 'Full',
  gradient: 'Simple',
  off: 'Off',
};
const GROUND_LABELS: Record<string, string> = {
  detailed: 'Detail',
  plain: 'Plain',
  grid: 'Grid',
};
const BACKGROUND_LABELS: Record<string, string> = {
  scenic: 'Scenic',
  ring: 'Low',
  off: 'Off',
};
const EFFECTS_LABELS: Record<string, string> = {
  full: 'Full',
  reduced: 'Low',
  off: 'Off',
};

/*
 * *Reset to default* used to reset the aim sensitivity alone, while being
 * labelled as though it reset everything — a control doing a fraction of what
 * it claims, and the fraction it did was the one setting a player is least
 * likely to want undone, since a sensitivity is a preference and a graphics tier
 * is a repair.
 *
 * `resetSettings` and `isDefaultSettings` live in `state/settings.ts` beside the
 * `DEFAULTS` they read, so this window keeps no list of its own — the copy is
 * always what goes stale, and a setting added there would otherwise quietly stop
 * being reset with nothing on screen to say so.
 */

</script>

<template>
  <div class="settings-scrim" @click.self="emit('close')">
    <section
      class="settings"
      data-test="settings-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-title"
    >
      <header class="win-head">
        <svg class="win-mark" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <g stroke="currentColor" stroke-width="1.3" stroke-linecap="round">
            <path d="M3 2.6v10.8M8 2.6v10.8M13 2.6v10.8" />
          </g>
          <circle cx="3" cy="10.4" r="1.9" fill="currentColor" />
          <circle cx="8" cy="5.2" r="1.9" fill="currentColor" />
          <circle cx="13" cy="9" r="1.9" fill="currentColor" />
        </svg>
        <h2 id="settings-title" class="win-title">Settings</h2>
        <span v-if="eyebrow" class="win-eyebrow">{{ eyebrow }}</span>
        <button
          type="button"
          class="win-close"
          data-test="settings-close"
          aria-label="Close settings"
          @click="emit('close')"
        >
          <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path
              d="M4 4l8 8M12 4l-8 8"
              fill="none"
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </header>

      <div class="win-body">
        <div class="win-pair">
          <fieldset class="fs">
            <legend>Controls</legend>
            <div class="fs-rows">
              <label class="row" for="aim-sensitivity">
                <span class="row-name">Sensitivity</span>
                <span class="row-value-figure" data-test="aim-sensitivity-value">
                  {{ aimSensitivity.toFixed(2) }}&times;
                </span>
              </label>
              <input
                id="aim-sensitivity"
                v-model.number="aimSensitivity"
                data-test="aim-sensitivity"
                type="range"
                :min="MIN_AIM_SENSITIVITY"
                :max="MAX_AIM_SENSITIVITY"
                step="0.05"
              />
            </div>
            <div class="fs-says">
              <p class="hint">
                How fast the mouse turns your view. Higher is quicker to spin
                round; lower is steadier to hold an aim. Your gun still swings at
                its own speed, and up-and-down tilt is not affected.
              </p>
              <p class="brief">
                How fast your view turns. The gun still swings at its own speed.
              </p>
            </div>
          </fieldset>

          <fieldset class="fs">
            <legend>Audio</legend>
            <div class="fs-rows">
              <div class="row">
                <span id="master-volume-name" class="row-name">Master volume</span>
                <span class="row-value-figure" data-test="volume-value">
                  {{ audioMuted ? 'Muted' : `${Math.round(masterVolume * 100)}%` }}
                </span>
                <div
                  class="seg"
                  role="group"
                  aria-label="Sound on or muted"
                  data-test="mute-toggle"
                >
                  <button
                    type="button"
                    :class="{ chosen: !audioMuted }"
                    data-test="sound-on"
                    @click="audioMuted = false"
                  >
                    On
                  </button>
                  <button
                    type="button"
                    :class="{ chosen: audioMuted }"
                    data-test="sound-off"
                    @click="audioMuted = true"
                  >
                    Mute
                  </button>
                </div>
              </div>
              <input
                id="master-volume"
                v-model.number="masterVolume"
                aria-labelledby="master-volume-name"
                data-test="volume"
                type="range"
                :min="MIN_VOLUME"
                :max="MAX_VOLUME"
                step="0.05"
                :disabled="audioMuted"
              />

              <div class="row">
                <span id="music-volume-name" class="row-name">
                  Music <kbd class="row-key">M</kbd>
                </span>
                <span class="row-value-figure" data-test="music-volume-value">
                  {{ musicMuted ? 'Muted' : `${Math.round(musicVolume * 100)}%` }}
                </span>
                <div
                  class="seg"
                  role="group"
                  aria-label="Music on or muted"
                  data-test="music-toggle"
                >
                  <button
                    type="button"
                    :class="{ chosen: !musicMuted }"
                    data-test="music-on"
                    @click="musicMuted = false"
                  >
                    On
                  </button>
                  <button
                    type="button"
                    :class="{ chosen: musicMuted }"
                    data-test="music-off"
                    @click="musicMuted = true"
                  >
                    Mute
                  </button>
                </div>
              </div>
              <input
                id="music-volume"
                v-model.number="musicVolume"
                aria-labelledby="music-volume-name"
                data-test="music-volume"
                type="range"
                :min="MIN_MUSIC_VOLUME"
                :max="MAX_MUSIC_VOLUME"
                step="0.05"
                :disabled="musicMuted || audioMuted"
              />
            </div>
            <div class="fs-says">
              <!-- Deliberately *not* mentioning the M key here. `.brief` is
                   compact-only, so a sentence about a keyboard shortcut would
                   appear on the one device that has no keyboard and nowhere
                   else — the same defect as the `Esc closes this` line this
                   window hides on a coarse pointer. The `<kbd>` on the Music
                   row states the binding at every size, which is where it
                   belongs. -->
              <p class="brief">
                The guns and the music keep their own levels, so one can go
                without the other.
              </p>
            </div>
          </fieldset>
        </div>

        <fieldset class="fs fs-video">
          <legend>Video</legend>
          <div class="fs-rows">
            <div v-if="canFullscreen" class="row">
              <span class="row-name">Fullscreen</span>
              <div
                class="seg"
                role="group"
                aria-label="Fullscreen on or off"
                data-test="fullscreen-toggle"
              >
                <button
                  type="button"
                  :class="{ chosen: fullscreen }"
                  data-test="fullscreen-on"
                  @click="fullscreen || toggleImmersive()"
                >
                  On
                </button>
                <button
                  type="button"
                  :class="{ chosen: !fullscreen }"
                  data-test="fullscreen-off"
                  @click="fullscreen && toggleImmersive()"
                >
                  Off
                </button>
              </div>
            </div>
            <div v-else-if="offerHomeScreen" class="row row-note" data-test="fullscreen-home-screen-hint">
              <span class="row-name">Fullscreen</span>
              <span class="row-note-text">
                Safari has no fullscreen button to offer here. Tap Share, then
                <strong>Add to Home Screen</strong> — that launches with no
                browser bars at all.
              </span>
            </div>
            <div class="row">
              <span class="row-name">Sky</span>
              <div class="seg" role="group" aria-label="Sky detail" data-test="sky-quality">
                <button
                  v-for="q in SKY_QUALITIES"
                  :key="q"
                  type="button"
                  :class="{ chosen: skyQuality === q }"
                  :data-test="`sky-${q}`"
                  @click="skyQuality = q"
                >
                  {{ SKY_LABELS[q] }}
                </button>
              </div>
            </div>
            <div class="row">
              <span class="row-name">Ground</span>
              <div class="seg" role="group" aria-label="Ground detail" data-test="ground-quality">
                <button
                  v-for="q in GROUND_QUALITIES"
                  :key="q"
                  type="button"
                  :class="{ chosen: groundQuality === q }"
                  :data-test="`ground-${q}`"
                  @click="groundQuality = q"
                >
                  {{ GROUND_LABELS[q] }}
                </button>
              </div>
            </div>
            <div class="row">
              <span class="row-name">Background</span>
              <div
                class="seg"
                role="group"
                aria-label="Background detail"
                data-test="background-quality"
              >
                <button
                  v-for="q in BACKGROUND_QUALITIES"
                  :key="q"
                  type="button"
                  :class="{ chosen: backgroundQuality === q }"
                  :data-test="`background-${q}`"
                  @click="backgroundQuality = q"
                >
                  {{ BACKGROUND_LABELS[q] }}
                </button>
              </div>
            </div>
            <div class="row">
              <span class="row-name">Effects</span>
              <div class="seg" role="group" aria-label="Effects detail" data-test="effects-quality">
                <button
                  v-for="q in EFFECTS_QUALITIES"
                  :key="q"
                  type="button"
                  :class="{ chosen: effectsQuality === q }"
                  :data-test="`effects-${q}`"
                  @click="effectsQuality = q"
                >
                  {{ EFFECTS_LABELS[q] }}
                </button>
              </div>
            </div>
            <div class="row">
              <span class="row-name">Hangar room</span>
              <div
                class="seg"
                role="group"
                aria-label="Hangar room on or off"
                data-test="hangar-room-toggle"
              >
                <button
                  type="button"
                  :class="{ chosen: hangarRoomEnabled }"
                  data-test="hangar-room-on"
                  @click="hangarRoomEnabled = true"
                >
                  On
                </button>
                <button
                  type="button"
                  :class="{ chosen: !hangarRoomEnabled }"
                  data-test="hangar-room-off"
                  @click="hangarRoomEnabled = false"
                >
                  Off
                </button>
              </div>
            </div>
            <div class="row">
              <span class="row-name">Garage previews</span>
              <div
                class="seg"
                role="group"
                aria-label="Garage previews on or off"
                data-test="preview-toggle"
              >
                <button
                  type="button"
                  :class="{ chosen: previewsEnabled }"
                  data-test="previews-on"
                  @click="previewsEnabled = true"
                >
                  On
                </button>
                <button
                  type="button"
                  :class="{ chosen: !previewsEnabled }"
                  data-test="previews-off"
                  @click="previewsEnabled = false"
                >
                  Off
                </button>
              </div>
            </div>
          </div>
          <div class="fs-says">
            <p class="hint">
              Turn these down if the game runs rough. Sky and ground make the
              biggest difference. Effects is the smoke and fire of a firefight —
              <em>Low</em> clears the smoke that hangs after an explosion,
              <em>Off</em> keeps flashes and tracers so you can still see where
              shots went. Background is the scenery beyond the walls:
              <em>Low</em> leaves a single ridge line, <em>Off</em> a bare
              horizon.
            </p>
            <p class="brief">
              Turn these down if the game runs rough. Sky and ground make the
              biggest difference; <em>Off</em> never hides a tracer or a muzzle
              flash.
            </p>
          </div>
        </fieldset>

        <fieldset v-if="session.player.value?.isAdmin" class="fs admin-fs">
          <legend>Operator</legend>
          <div class="fs-rows">
            <div class="row">
              <span class="row-name">Admin panel</span>
              <button
                type="button"
                class="admin-link"
                data-test="admin-panel-link"
                @click="openAdmin"
              >
                Open
                <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
                  <path
                    d="M3.5 6h5M6 3.5L8.5 6 6 8.5"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div class="fs-says">
            <p class="hint">
              Players, content, billing, settings and meta tags — signed in as
              you, no second login.
            </p>
            <p class="brief">Players, content, billing and meta tags.</p>
          </div>
        </fieldset>

        <button
          type="button"
          class="reset"
          data-test="settings-reset"
          :disabled="isDefaultSettings()"
          @click="resetSettings()"
        >
          Reset to default
        </button>
      </div>

      <footer v-if="$slots.actions" class="win-actions">
        <slot name="actions" />
      </footer>

      <p class="win-hint"><kbd>Esc</kbd> closes this</p>
    </section>
  </div>
</template>

<style scoped>
/* ─── The window ─────────────────────────────────────────────────────────── */

/*
 * **Flex rather than `place-items: center` on a grid, and that is the one line
 * here that had to be measured.** A grid with an implicit `auto` row sizes the
 * row from its content, so the card's `max-height: 100%` resolved against an
 * indefinite height and was dropped entirely: on a 375px-tall landscape phone
 * the window came out **568px tall**, overflowing the screen, with the footer's
 * Resume and Leave below the bottom edge — and centring silently fell back to
 * top-aligned, because a browser will not centre an overflowing item into a
 * position where the start of it cannot be reached. A flex container's height is
 * definite (`inset: 0`), so the percentage resolves and the card clamps.
 */
.settings-scrim {
  position: absolute;
  inset: 0;
  /*
   * 60, and both neighbours are measured rather than guessed. The garage's own
   * overlays sit at 40 and its title bar at 3, so anything above 40 clears both
   * — and so did `ui/MusicControl.vue`, which is `position: fixed`, `z-index: 40`
   * and mounted *after* this in the tree: at 40 the chip it used to pin to the
   * bottom centre painted straight through this window's footer, over the account
   * sheet's *Profile & account* on a phone. Redundantly, too — the music bed's own
   * On/Mute is a row in here, which is what eventually retired the chip outright.
   * The number stays: what it is for is the ordering against everything at 40 and
   * below. Under `ConfirmPurchase` and `ConfirmSignOut` at 100, which are the
   * highest things in the app and stay that way.
   */
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  /* The battle HUD as a whole is transparent to the mouse; this one layer is
     not, and that is what keeps a click on the backdrop off the canvas
     underneath — where it would request pointer lock and fire the gun. */
  pointer-events: auto;
  background: rgba(6, 8, 11, 0.74);
  backdrop-filter: blur(3px);
}

.settings {
  display: flex;
  flex-direction: column;
  /*
   * Wide rather than tall. 46rem is the width at which the two top groups sit
   * side by side with the video grid two-across beneath them; below it the
   * `auto-fit` tracks collapse on their own, so there is no breakpoint to keep
   * in step with this number.
   */
  width: min(46rem, 100%);
  max-height: 100%;
  border: 1px solid var(--steel-600);
  border-top: 2px solid var(--accent);
  border-radius: 4px;
  background: rgba(12, 14, 18, 0.97);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.6);
  color: var(--steel-200);
  font-size: 0.8rem;
  text-align: left;
  /* Every segmented choice's width, in one place. See `.seg`. */
  --seg-w: 10.5rem;
}

.win-head {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid var(--steel-700);
}

.win-mark {
  flex: none;
  width: 1.15rem;
  height: 1.15rem;
  color: var(--accent);
}

.win-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: #fff;
}

/* The host's kicker — `Battle menu`. Quiet: it says which door this was opened
   through, which is context rather than the subject. */
.win-eyebrow {
  color: var(--steel-400);
  font-size: 0.64rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.win-eyebrow::before {
  content: '·';
  margin-right: 0.45rem;
}

.win-close {
  margin-left: auto;
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  flex: none;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 3px;
  background: transparent;
  color: var(--steel-400);
}

.win-close svg {
  width: 1rem;
  height: 1rem;
}

.win-close:hover,
.win-close:focus-visible {
  border-color: var(--steel-600);
  color: #fff;
}

.win-body {
  /* The one scrolling surface. Head, actions and hint stay put, so the way out
     is on screen whatever the viewport has left for the controls. */
  overflow-y: auto;
  padding: 0.9rem 1rem 1rem;
}

/* ─── Groups ─────────────────────────────────────────────────────────────── */

/*
 * Two columns, stated rather than `auto-fit`.
 *
 * `repeat(auto-fit, minmax(16rem, 1fr))` was the first shape and it is wrong for
 * the video group specifically: at a landscape phone's 812px it fits *three*
 * tracks, so Sky, Ground and Effects went across the top and Garage previews sat
 * alone on a second row. Four controls want a 2x2. One breakpoint that both
 * grids share is easier to keep true than a minimum width that has to produce
 * exactly two tracks at every size the game is played at.
 */
.win-pair {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.8rem;
}

/*
 * The two top groups stretch to the taller of them, and Controls holds one
 * control against Audio's four — so `justify-content: center` shares that slack
 * above and below rather than pooling it under the last row, where a box with
 * 90px of nothing at the bottom reads as something that failed to render.
 *
 * A no-op on the group that is full, which is why it is one rule and not a
 * special case. **Compact undoes both halves** — see the phone block at the
 * foot of this file: there the groups are stacked, so there is no taller
 * neighbour to match and nothing to centre against.
 */
.fs {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  margin: 0;
  padding: 0.35rem 0.75rem 0.7rem;
  border: 1px solid var(--steel-700);
  border-radius: 3px;
}

.fs legend {
  padding: 0 0.4rem;
  color: var(--steel-400);
  font-size: 0.6rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.fs-video {
  margin-top: 0.8rem;
}

/*
 * The controls of a group, and its prose, as two named boxes.
 *
 * They exist so the compact layout can *place* them — a quarter of the width for
 * the words and three quarters for the controls — which a flat list of rows
 * cannot express. On a desktop they are simply two stacked blocks and the shape
 * is what it always was: the rows, then the paragraph under them.
 *
 * The rows come first in the DOM because that is the desktop reading order; the
 * compact block swaps the columns with `grid-column`, never with `order`, so the
 * tab sequence still runs label-then-control.
 */
.fs-rows {
  min-width: 0;
}

/*
 * Video's rows are a 2x2 on a desktop rather than a column of seven.
 *
 * This used to be a `.vgrid` wrapper inside the group; it is the group's own row
 * box now, which is one element fewer and means the compact rule that turns it
 * back into a column has the same selector shape as every other group's.
 */
.fs-video .fs-rows {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.15rem 1.4rem;
}

/* Narrower than two groups can share — a phone held upright. */
@media (max-width: 34rem) {
  .win-pair,
  .fs-video .fs-rows {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* ─── Rows ───────────────────────────────────────────────────────────────── */

.row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-height: 2rem;
}

/*
 * The name takes the slack and the readout and the choice sit hard right, which
 * is what lines up every value and every segmented control into two columns
 * without a grid having to be declared per row shape — the rows hold two, three
 * or (Operator) two-with-a-button, and `margin-left: auto` is indifferent to
 * which.
 */
.row-name {
  /* Shrinkable, so a very narrow window wraps the label rather than pushing the
     fixed-width choice past the group's edge. */
  min-width: 0;
  color: var(--steel-200);
  font-size: 0.8rem;
}

/* The one row that is a sentence rather than a control: no choice sits at its
   trailing edge, so it drops `.row`'s single-line assumption and wraps under
   the label instead of squeezing beside it. */
.row-note {
  flex-direction: column;
  align-items: flex-start;
  gap: 0.3rem;
  min-height: 0;
}

.row-note-text {
  font-size: 0.76rem;
  line-height: 1.4;
  color: var(--steel-400);
}

.row-note-text strong {
  color: var(--steel-200);
  font-weight: 600;
}

.row-value-figure {
  margin-left: auto;
  font-variant-numeric: tabular-nums;
  color: var(--accent);
  font-weight: 600;
}

/* The choice is last, so a row carrying both a figure and a choice keeps the
   number beside the words and the buttons at the edge. */
.row .seg,
.row .admin-link {
  margin-left: auto;
}

.row .row-value-figure ~ .seg {
  margin-left: 0;
}

/* The key that already did this, stated where the control is. */
.row-key {
  margin-left: 0.35rem;
  padding: 0 0.28rem;
  border: 1px solid var(--steel-600);
  border-radius: 2px;
  color: var(--steel-400);
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: 0.6rem;
}

/*
 * The prose. Two versions of it, and only ever one on screen.
 *
 * `.hint` is the desktop paragraph — the one that has room to say what *Low* and
 * *Off* actually remove. `.brief` is a sentence written for the quarter-width
 * column a phone gives it: the long one at ~160px is taller than the seven video
 * rows beside it and would drive the height of the whole group, which is how a
 * description ends up being the subject of a settings window.
 *
 * Two paragraphs rather than a line clamp, because a clamp truncates mid-clause
 * and the half it keeps is chosen by arithmetic. The pair is asserted in
 * `settings-panel.test.ts` so neither can quietly go missing.
 */
.hint,
.brief {
  margin: 0.5rem 0 0;
  color: var(--steel-400);
  font-size: 0.68rem;
  line-height: 1.45;
}

.hint em,
.brief em {
  font-style: normal;
  font-weight: 600;
  color: var(--steel-200);
}

/* Compact's, and nowhere else. */
.brief {
  display: none;
}

/* ─── Segmented choices ──────────────────────────────────────────────────── */

/*
 * **Every choice is the same width, and every button inside one is an equal
 * share of it.**
 *
 * They used to be sized by their labels: `min-width` a button plus whatever the
 * word needed, which came out at 83px for a two-way and 124–134px for the four
 * three-way video dials — measured, all different, all in one column. So the
 * video group read as a ragged right edge, and `SCENIC` was a wider button than
 * `LOW` beside it in the same control.
 *
 * A grid of `1fr` tracks inside a container of stated width fixes both at once,
 * and it is deliberately *one* width for two-way and three-way alike: what lines
 * up down the group is the control, so a two-way simply gets fatter buttons. The
 * variable is on `.settings` so the whole window can be retuned in one place.
 *
 * 10.5rem is the narrowest that holds the longest three-way — `DETAIL PLAIN
 * GRID` at 0.64rem with 0.1em of tracking wants ~55px a cell, and this gives
 * 56 — and it clears the 44px touch floor by 12px, which is why the touch block
 * below no longer widens anything.
 */
.seg {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  flex: none;
  width: var(--seg-w);
  /* A group narrower than the control itself would otherwise overflow it. */
  max-width: 100%;
  gap: 1px;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  overflow: hidden;
}

.seg button {
  min-width: 0;
  padding: 0.3rem 0.35rem;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: var(--steel-200);
  font-size: 0.64rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.seg button:hover:not(.chosen) {
  background: rgba(255, 255, 255, 0.06);
}

.seg button.chosen {
  background: var(--accent);
  color: #14171c;
  font-weight: 600;
}

/* ─── The Operator row's one button ──────────────────────────────────────── */

/*
 * **This had no rule at all, and nobody had looked at it.** The Operator group
 * is `v-if="isAdmin"`, and the e2e account is not — so `.admin-link` rendered as
 * a *browser-default* button inside a styled window: white slab, black text,
 * `Open` wrapping onto one line with the arrow on the next, 48x56. Found by
 * signing in as `admin@tactus.dev` and looking, which is the only way it could
 * have been found.
 *
 * Styled as the window's own quiet button — the same border, radius and tracking
 * `.reset` wears — rather than as a `.seg`, because it is one action and not a
 * choice between states. The arrow stays inline (`inline-flex`), which is what
 * stopped the wrap.
 */
.admin-link {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.7rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: transparent;
  color: var(--steel-200);
  font: inherit;
  font-size: 0.68rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  white-space: nowrap;
}

.admin-link svg {
  width: 0.75rem;
  height: 0.75rem;
  flex: none;
}

.admin-link:hover,
.admin-link:focus-visible {
  border-color: var(--accent);
  color: var(--accent);
}

/* ─── Sliders ────────────────────────────────────────────────────────────── */

/*
 * Styled rather than left to `accent-color`, because the two hosts sit on
 * different grounds and a native range is a different object on every platform:
 * the same declaration is a flat orange bar in Chrome, a blue-tinted one in
 * Safari and a stepped groove on Windows. One drawing, like every other mark
 * here.
 */
.settings input[type='range'] {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 1.4rem;
  margin: 0.1rem 0 0.2rem;
  background: transparent;
}

.settings input[type='range']::-webkit-slider-runnable-track {
  height: 3px;
  border-radius: 2px;
  background: var(--steel-600);
}

.settings input[type='range']::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  /* Half the thumb less half the track, so the knob rides the centre line. */
  margin-top: -5.5px;
  border: 1px solid var(--steel-900);
  border-radius: 50%;
  background: var(--steel-200);
}

.settings input[type='range']::-moz-range-track {
  height: 3px;
  border-radius: 2px;
  background: var(--steel-600);
}

.settings input[type='range']::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border: 1px solid var(--steel-900);
  border-radius: 50%;
  background: var(--steel-200);
}

.settings input[type='range']:hover::-webkit-slider-thumb,
.settings input[type='range']:focus-visible::-webkit-slider-thumb {
  background: var(--accent);
}

.settings input[type='range']:hover::-moz-range-thumb,
.settings input[type='range']:focus-visible::-moz-range-thumb {
  background: var(--accent);
}

/* Still readable, plainly not in charge. */
.settings input[type='range']:disabled {
  opacity: 0.35;
  cursor: default;
}

/* ─── Reset, actions, hint ───────────────────────────────────────────────── */

.reset,
.win-actions :deep(button) {
  padding: 0.6rem 0.9rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: transparent;
  color: var(--steel-200);
  font: inherit;
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.reset {
  width: 100%;
  margin-top: 0.9rem;
}

.reset:hover:not(:disabled) {
  border-color: var(--steel-400);
  color: #fff;
}

.reset:disabled {
  opacity: 0.35;
  cursor: default;
}

/*
 * The host's own buttons, styled here so the two screens agree on what a button
 * in this window looks like. `:deep` because the slot's content carries the
 * host's scope id, not this component's — the alternative is each host restating
 * the same six declarations, which is how two windows come to look like two
 * products.
 */
.win-actions {
  padding: 0 1rem 0.2rem;
}

.win-actions :deep(button):hover {
  border-color: var(--steel-400);
  color: #fff;
}

.win-hint {
  margin: 0.7rem 0 0.8rem;
  text-align: center;
  color: var(--steel-400);
  font-size: 0.62rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.win-hint kbd {
  margin-right: 0.35rem;
  padding: 0 0.3rem;
  border: 1px solid var(--steel-600);
  border-radius: 2px;
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: 0.6rem;
}

/* ─── A phone held sideways ──────────────────────────────────────────────── */

/*
 * ~670x375, which has width to spare and almost none of the other thing. The
 * padding comes in and the window takes the full width rather than the 46rem a
 * desktop gives it.
 *
 * A height query rather than a width one, and the same 520px `GarageScreen`
 * resolves its own compact mode at: a wide short window is a phone on its side
 * and a narrow tall one is a phone upright, and it is the short axis that
 * decides whether this fits.
 *
 * ── One column of groups, each split a quarter to three quarters ────────────
 *
 * Owner instruction, 2026-09-02, and it replaces the three-column layout this
 * block used to grow into at `min-width: 40rem`. That layout put all eleven
 * controls on screen at once — 184px of scroll became 64 — and the cost was
 * three ~205px columns in which a label and a three-way choice had to share the
 * row: the segmented buttons were floored at 44px to stop them clipping, the
 * prose was dropped outright, and Controls sat in a column of its own holding
 * one slider. Two whole media blocks existed to keep that from breaking.
 *
 * What is here instead is Controls, Audio and Video **stacked**, each group a
 * `1fr 3fr` grid: the group's name and one sentence about it in the quarter, its
 * rows in the three quarters. At 667px that is a ~160px column of words beside a
 * ~470px column of controls, so a row is a label and a full-width choice rather
 * than a label and a squeezed one — and the choice can keep the same 10.5rem
 * every other choice in the window has.
 *
 * **It is bought with scroll, deliberately.** Eleven rows one per line is about
 * 640px against ~300px of body, so the window scrolls two screens where it used
 * to scroll a fifth of one. That is the trade the instruction makes, and it is
 * survivable because the header, the actions and the way out are all pinned
 * outside `.win-body` — the way out is on screen at every scroll position, which
 * is the only property this window cannot give up.
 *
 * `align-content: start` is the other half of the instruction: the groups no
 * longer stretch to a common height and nothing is centred inside them, so a
 * short group is short and every row of every group starts at the top of it.
 */
@media (max-height: 520px) {
  .settings-scrim {
    padding: 0.4rem;
  }

  .settings {
    width: 100%;
    font-size: 0.76rem;
  }

  .win-head {
    padding: 0.5rem 0.7rem;
  }

  .win-body {
    padding: 0.55rem 0.7rem 0.7rem;
  }

  /* One column of groups. Video joins the other two in the same stack, so its
     desktop top margin becomes the stack's own gap. */
  .win-pair {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.5rem;
  }

  .fs-video,
  .admin-fs {
    margin-top: 0.5rem;
  }

  /*
   * The quarter and the three quarters.
   *
   * `display: grid` on a `<fieldset>` leaves the rendered `<legend>` where the
   * browser draws it — notched into the block-start border — rather than making
   * it a grid item, so the group's name sits above the two columns and the
   * columns are exactly the two boxes placed below. Stated `grid-row: 1` on both
   * anyway, so a browser that *did* treat the legend as an item could not push
   * either column into a second row.
   */
  .fs {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 3fr);
    column-gap: 0.7rem;
    align-items: start;
    align-content: start;
    padding: 0.2rem 0.6rem 0.5rem;
  }

  .fs-says {
    grid-column: 1;
    grid-row: 1;
  }

  .fs-rows {
    grid-column: 2;
    grid-row: 1;
  }

  /* Video's 2x2 becomes a column: it is the three-quarter column now, and two
     tracks of it would be two labels and two choices in ~470px. */
  .fs-video .fs-rows {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }

  /* The long paragraph gives way to the short one. See `.brief`. */
  .hint {
    display: none;
  }

  .brief {
    display: block;
    margin: 0.1rem 0 0;
  }

  .win-actions {
    padding: 0 0.7rem;
  }

  .win-hint {
    margin: 0.5rem 0 0.55rem;
  }

  .row {
    min-height: 1.8rem;
  }

  .settings input[type='range'] {
    height: 1.15rem;
  }

  .reset {
    margin-top: 0.5rem;
  }
}

/*
 * Touch targets. A finger needs 44px and a mouse does not, so this is asked of
 * the device rather than plumbed through as a prop — the same question
 * `state/device.ts` asks in script, in the form CSS can ask it.
 */
@media (hover: none) and (pointer: coarse) {
  .row {
    min-height: 44px;
  }

  /*
   * Height only. The width used to be floored here too, at a 3.6rem comfort
   * figure, and it was the cause of a clipping bug worth remembering: the
   * three-column compact layout gave a row ~205px, three floored buttons came to
   * ~180px plus the label, and the Ground and Effects rows rendered their last
   * button past the panel's edge — `GRID` cut mid-word, `OFF` unreachable, on
   * exactly the viewport that layout existed for. It needed a fourth media block
   * to undo. `.seg` now states one width for every choice in the window and
   * divides it into equal tracks, so 10.5rem over three cells is 56px a button
   * and the floor is met by arithmetic rather than by a second rule.
   */
  .seg button {
    min-height: 44px;
  }

  .win-close {
    width: 44px;
    height: 44px;
  }

  .settings input[type='range'] {
    height: 44px;
  }

  .settings input[type='range']::-webkit-slider-thumb {
    width: 22px;
    height: 22px;
    margin-top: -9.5px;
  }

  .settings input[type='range']::-moz-range-thumb {
    width: 22px;
    height: 22px;
  }

  .reset,
  .admin-link,
  .win-actions :deep(button) {
    min-height: 44px;
  }

  /*
   * There is no Escape key on a phone, so the line saying it closes this is not
   * merely wasted height — it is wrong. The X in the header and a tap on the
   * backdrop are both still there, and both are what a touch user reaches for.
   *
   * Keyed on the pointer rather than on the viewport, deliberately: a laptop
   * window 500px tall matches the height query above and does have the key.
   */
  .win-hint {
    display: none;
  }
}

</style>
